<template>
  <div class="rules-drawer-panel">
    <div class="rules-header">
      <h2 class="rules-title">{{ $t('tournamentRules_RulesDialog') }}</h2>
      <button class="rules-close-btn" @click="$emit('close')">
        <i class="fa-regular fa-times"></i>
      </button>
    </div>

    <div class="rules-body">
      <div v-if="isHantecTournament" class="hantec-rules">
        <p class="rules-subtitle mb-4">{{ $t('rulesDescription') }}</p>
        <div class="rules-link-container">
          <p class="mb-3">{{ $t('fullRulesAvailable') }}</p>
          <a
            :href="hantecRulesLink"
            target="_blank"
            class="rules-external-link"
            :style="buttonColorStyle"
          >
            <i class="fa-regular fa-external-link-alt mr-2"></i>
            {{ $t('viewFullRules') }}
          </a>
        </div>
      </div>
      <div v-else-if="isTwTournament"></div>
      <template v-else-if="rules">
        <p class="rules-subtitle">{{ $t('rulesDescription') }}</p>
        <div class="rules-html-content" v-html="rules"></div>
      </template>
      <p v-else class="no-rules">{{ $t('noRulesAvailable') }}</p>
    </div>

    <div class="rules-footer">
      <button class="rules-footer-btn rules-close-action" @click="$emit('close')">
        {{ $t('close_RulesDialog') }}
      </button>
      <button
        v-if="false && adminMode"
        class="rules-footer-btn rules-create-action"
        :style="buttonColorStyle"
        @click="$emit('close'); goToSettings()"
      >
        {{ $t('createNewRule') || 'Create New Rule' }}
        <i class="fa-solid fa-plus ml-2"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RulesDialog',
  props: {
    rules: {
      type: String,
      default: null
    },
    adminMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isHantecTournament() {
      return this.$store.state.tournament &&
        this.$store.state.tournament.tournamentOrganizerId === 125;
    },
    isTwTournament() {
      return this.$store.state.tournament &&
        this.$store.state.tournament.tournamentId === 401 && this.$store.state.clubMember && this.$store.state.clubMember.rol !== 'ADMIN';
    },
    hantecRulesLink() {
      if (!this.isHantecTournament) return '';
      const locale = this.$i18n.locale;
      const baseUrl = 'https://hmarkets.com';
      switch (locale) {
        case 'es':
          return `${baseUrl}/es/terms-and-conditions/hantec-markets-demo-trading-tournament/`;
        case 'pt':
          return `${baseUrl}/pt/terms-and-conditions/hantec-markets-demo-trading-tournament/`;
        case 'en':
        default:
          return `${baseUrl}/terms-and-conditions/hantec-markets-demo-trading-tournament/`;
      }
    },
    organizerColorExists() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      return colors && (colors.primary || colors.secondary);
    },
    buttonColorStyle() {
      if (this.organizerColorExists) {
        const colors = this.$store.state.tournament.tournamentSettings.colors;
        const isDark = this.$vuetify.theme.dark;
        const color = isDark && colors.secondary ? colors.secondary : colors.primary;
        if (!color) return {};
        const cleanColor = color.replace('#', '');
        const fullColor = `#${cleanColor}`;
        return {
          backgroundColor: `${fullColor}`,
          borderColor: `${fullColor}`,
          color: `${this.getContrastYIQ(cleanColor) === 'black' ? '#000' : '#fff'}`
        };
      }
      return {};
    },
  },
  methods: {
    getContrastYIQ(hexcolor) {
      if (!hexcolor) return 'black';
      hexcolor = hexcolor.replace('#', '');
      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (yiq >= 128) ? 'black' : 'white';
    },
    goToSettings() {
      const tournamentId = this.$store.state.tournament?.tournamentId;
      if (tournamentId) {
        this.$router.push({ name: 'TournamentSettings', params: { tournamentId } });
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.rules-drawer-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--v-mainWhite-base);
  font-family: 'Aeonik', sans-serif;
}

.rules-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 16px;

  .rules-title {
    font-size: 22px;
    font-weight: 600;
    color: var(--v-mainBlack-base);
    margin: 0;
  }

  .rules-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 18px;
    color: #666;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;

    &:hover {
      color: #333;
    }
  }
}

.rules-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;
  font-family: 'Avenir', sans-serif;

  .rules-subtitle {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-mainBlack-base);
    margin-bottom: 20px;
  }

  .rules-html-content {
    font-size: 14px;
    line-height: 1.7;
    color: #444;

    ::v-deep ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    ::v-deep li {
      padding: 8px 0;
      border-bottom: none;
    }
  }

  .no-rules {
    text-align: center;
    color: #999;
    padding: 40px 0;
    font-size: 14px;
  }

  .rules-external-link {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    background-color: #ebebeb;
    color: #14161B;
    transition: all 0.2s ease;

    &:hover {
      background-color: #ddd;
    }
  }
}

.rules-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #eee;

  .rules-footer-btn {
    padding: 10px 24px;
    border-radius: 8px;
    font-family: 'Aeonik', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .rules-close-action {
    background-color: #fff;
    color: #14161B;
    border: 1px solid #ccc;

    &:hover {
      background-color: #f5f5f5;
    }
  }

  .rules-create-action {
    background-color: #c03131;
    color: #fff;
    border: 1px solid #c03131;

    &:hover {
      opacity: 0.9;
    }

    i {
      font-size: 12px;
    }
  }
}

@media (max-width: 600px) {
  .rules-header {
    padding: 16px 16px 12px;

    .rules-title {
      font-size: 18px;
    }
  }

  .rules-body {
    padding: 0 16px 16px;
  }

  .rules-footer {
    padding: 12px 16px;
    flex-direction: column;

    .rules-footer-btn {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>

<style lang="scss">
.theme--dark {
  .rules-drawer-panel {
    background-color: var(--v-mainWhite-base);
  }

  .rules-close-action {
    background-color: #303740;
    color: #fff;
    border-color: #3a3f47;

    &:hover {
      background-color: #3a424d;
    }
  }
}
</style>
